<template>
  <div class="kanban-board">
    <div class="kanban-columns">
      <div v-for="status in statuses" :key="status.value" class="kanban-column">
        <div class="kanban-column-header" :style="{backgroundColor: status.color}">
          <h6 class="mb-0 text-white">{{ status.label }}</h6>
          <span class="badge bg-white text-dark">{{ getTasksByStatus(status.value).length }}</span>
        </div>
        <div 
          class="kanban-column-body"
          @drop="onDrop($event, status.value)"
          @dragover="onDragOver($event)"
          @dragenter="onDragEnter($event)"
          @dragleave="onDragLeave($event)"
        >
          <div 
            v-for="assignment in getTasksByStatus(status.value)" 
            :key="assignment.id" 
            class="kanban-card"
            :draggable="helper.checkAccess('edit_tasks') > 0"
            @dragstart="onDragStart($event, assignment)"
            @dragend="onDragEnd($event)"
            @click="$emit('view', assignment.task)"
          >
            <div class="task-card-heading">
              <span class="task-state-icon" :style="getStatusIconStyle(assignment.task.status)">
                <AppIcon :name="getStatusIcon(assignment.task.status)" class="bi" :class="getStatusIcon(assignment.task.status)" />
              </span>
              <strong class="task-title" draggable="false">{{ assignment.task.title }}</strong>
            </div>
            <div v-if="assignment.task.project" class="task-project" :title="assignment.task.project.name">
              <AppIcon name="bi-folder" class="bi bi-folder" />
              <span>{{ assignment.task.project.name }}</span>
            </div>
            <p class="task-description">{{ assignment.task.description || $t('no_description') }}</p>

            <div class="task-card-footer">
              <div class="task-assignee">
                <span class="assignee-avatar">{{ currentUserInitial }}</span>
                <span class="assignee-name">{{ currentUserName }}</span>
              </div>
              <span class="task-due-date" :class="{ overdue: isOverdue(assignment.task.due_date) }">
                <AppIcon name="bi-calendar3" class="bi bi-calendar3" />
                {{ formatDate(assignment.task.due_date) }}
              </span>
            </div>
          </div>
          <div v-if="!getTasksByStatus(status.value).length" class="text-center text-muted py-3">
            <small>{{$t('no_tasks')}}</small>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import helper from '../../helper'
import { getKanbanStatuses } from '../../helpers/taskStatusHelper'

export default {
  name: 'MyTaskKanban',
  components: {
  },
  props: {
    tasks: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      helper,
      draggedItem: null
    }
  },
  computed: {
    statuses() {
      return getKanbanStatuses(this.$store);
    },
    currentUserName() {
      const user = this.$store.getters.USER
      return user && user.name ? user.name : this.$t('unassigned')
    },
    currentUserInitial() {
      return this.currentUserName.split(' ').map(part => part.charAt(0)).join('').slice(0, 2).toUpperCase()
    }
  },
  methods: {
    // Drag and Drop Methods
    onDragStart(event, assignment) {
      if (helper.checkAccess('edit_tasks') <= 0) return
      this.draggedItem = assignment
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/html', event.target.innerHTML)
      event.target.classList.add('dragging')
    },
    onDragEnd(event) {
      event.target.classList.remove('dragging')
    },
    onDragOver(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    },
    onDragEnter(event) {
      if (event.target.classList.contains('kanban-column-body')) {
        event.target.classList.add('drag-over')
      }
    },
    onDragLeave(event) {
      if (event.target.classList.contains('kanban-column-body')) {
        event.target.classList.remove('drag-over')
      }
    },
    async onDrop(event, newStatus) {
      event.preventDefault()
      event.currentTarget.classList.remove('drag-over')
      
      if (helper.checkAccess('edit_tasks') <= 0 || !this.draggedItem || !this.draggedItem.task) return
      
      const task = this.draggedItem.task
      const oldStatus = task.status
      const oldProgress = task.progress
      
      // Don't do anything if dropped in the same column
      if (oldStatus === newStatus) {
        this.draggedItem = null
        return
      }

      // Optimistic update
      task.status = newStatus
      if (newStatus === 'completed' && task.progress !== 100) {
        task.progress = 100
      }
      this.$emit('task-updated')
      this.draggedItem = null
      
      try {
        const updates = { status: newStatus }
        if (newStatus === 'completed' && oldProgress !== 100) {
          updates.progress = 100
        }

        // Keep the optimistic local state; reloading the task list here makes the
        // whole board flash after every move.
        await this.helper.customPatch(
          `${process.env.VUE_APP_API}/tasks/${task.id}`,
          updates,
          { showToast: true }
        )
      } catch (error) {
        // Revert on error
        task.status = oldStatus
        task.progress = oldProgress
        this.$emit('task-updated')
        alert('Error updating task status: ' + error)
        this.helper.showToast(this.$t('error_updating_task'), 'error')
      }
    },
    getTasksByStatus(status) {
      return this.tasks.filter(assignment => assignment.task && assignment.task.status === status)
    },
    getStatusIcon(status) {
      if (status === 'completed') return 'bi-check-lg'
      if (status === 'in_progress' || status === 'review') return 'bi-clock'
      if (status === 'cancelled') return 'bi-x-lg'
      return 'bi-circle-fill'
    },
    getStatusIconStyle(statusValue) {
      const status = this.statuses.find(item => item.value === statusValue)
      const color = status ? status.color : '#64748b'
      return { backgroundColor: color, boxShadow: `0 5px 12px ${color}33` }
    },
    formatDate(date) {
      if (!date) return '-'
      return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(date))
    },
    isOverdue(dueDate) {
      if (!dueDate) return false
      return new Date(dueDate) < new Date()
    }
  }
}
</script>

<style scoped>
.kanban-board {
  overflow-x: auto;
  padding-bottom: 20px;
}

.kanban-columns {
  display: flex;
  gap: 12px;
  min-width: 100%;
}

.kanban-column {
  flex: 1;
  min-width: 250px;
  background: #f4f7fb;
  border-radius: 10px;
  overflow: hidden;
}

.kanban-column-header {
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.kanban-column-body {
  padding: 10px;
  min-height: 400px;
  max-height: 600px;
  overflow-y: auto;
}

.kanban-card {
  background: white;
  border: 1px solid #e8edf4;
  border-radius: 11px;
  padding: 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(31,45,61,0.06);
  cursor: move;
  transition: all 0.2s;
}

.kanban-card:hover {
  border-color: #d7e2f0;
  box-shadow: 0 5px 14px rgba(31,45,61,0.1);
  transform: translateY(-1px);
}

.kanban-card.dragging {
  opacity: 0.5;
  cursor: grabbing;
}

.kanban-column-body.drag-over {
  background-color: #e3f2fd;
  border: 2px dashed #2196F3;
}

.task-card-heading {
  display: flex;
  align-items: center;
  gap: 9px;
}

.task-state-icon {
  display: inline-grid;
  width: 29px;
  height: 29px;
  flex: 0 0 29px;
  place-items: center;
  border-radius: 50%;
  color: #fff;
  font-size: 13px;
}

.task-state-icon .bi-circle-fill {
  font-size: 9px;
}

.task-title {
  color: #17233a;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.task-project {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 5px;
  margin: 6px 0 0 38px;
  color: #526781;
  font-size: 11px;
  font-weight: 600;
}

.task-project span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-description {
  min-height: 18px;
  margin: 6px 0 10px 38px;
  color: #6b7c93;
  font-size: 11px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.task-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-left: 38px;
}

.task-assignee {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
}

.assignee-avatar {
  display: grid;
  width: 27px;
  height: 27px;
  flex: 0 0 27px;
  place-items: center;
  border-radius: 50%;
  background: #e4ddff;
  color: #6750a4;
  font-size: 9px;
  font-weight: 750;
}

.assignee-name {
  overflow: hidden;
  color: #4e5f75;
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-due-date {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 5px;
  color: #526781;
  font-size: 11px;
  font-weight: 600;
}

.task-due-date i {
  font-size: 14px;
}

.task-due-date.overdue {
  color: #dc3545;
}
</style>
